import { RiderResultsRetainedFilter } from '../models/RiderResults';

export const RIDER_RESULTS_RETAINED_FILTER_KEY = 'ezrunpbra:riderResultsFavorite';

const cleanNames = (names: string[]) => Array.from(new Set(names.map((name) => name.trim()).filter(Boolean)));

const isRetainedFilter = (value: unknown): value is RiderResultsRetainedFilter => {
  const candidate = value as Partial<RiderResultsRetainedFilter>;
  return (
    Array.isArray(candidate.riderNames) &&
    candidate.riderNames.every((riderName) => typeof riderName === 'string') &&
    Array.isArray(candidate.horseNames) &&
    candidate.horseNames.every((horseName) => typeof horseName === 'string') &&
    candidate.riderNames.length + candidate.horseNames.length > 0 &&
    typeof candidate.updatedAt === 'string' &&
    candidate.version === 4
  );
};

const normalizeStoredFilter = (value: unknown): RiderResultsRetainedFilter | null => {
  const candidate = value as Partial<RiderResultsRetainedFilter> & { savedAt?: unknown };
  if (
    !Array.isArray(candidate.riderNames) ||
    !candidate.riderNames.every((riderName) => typeof riderName === 'string') ||
    !Array.isArray(candidate.horseNames) ||
    !candidate.horseNames.every((horseName) => typeof horseName === 'string')
  ) {
    return null;
  }

  const riderNames = cleanNames(candidate.riderNames);
  const horseNames = cleanNames(candidate.horseNames);
  if (riderNames.length + horseNames.length === 0) return null;

  return {
    riderNames,
    horseNames,
    updatedAt:
      typeof candidate.updatedAt === 'string'
        ? candidate.updatedAt
        : typeof candidate.savedAt === 'string'
          ? candidate.savedAt
          : new Date().toISOString(),
    version: 4,
  };
};

export const loadRiderResultsRetainedFilter = (
  storage: Storage = window.localStorage,
): RiderResultsRetainedFilter | null => {
  try {
    const rawFilter = storage.getItem(RIDER_RESULTS_RETAINED_FILTER_KEY);
    if (!rawFilter) return null;

    const parsedFilter = JSON.parse(rawFilter);
    const filterCandidate = Array.isArray(parsedFilter) ? parsedFilter[0] : parsedFilter;
    return isRetainedFilter(filterCandidate) ? filterCandidate : normalizeStoredFilter(filterCandidate);
  } catch {
    return null;
  }
};

export const saveRiderResultsRetainedFilter = (
  riderNames: string[],
  horseNames: string[],
  storage: Storage = window.localStorage,
): RiderResultsRetainedFilter | null => {
  const cleanedRiderNames = cleanNames(riderNames);
  const cleanedHorseNames = cleanNames(horseNames);
  if (cleanedRiderNames.length + cleanedHorseNames.length === 0) {
    return clearRiderResultsRetainedFilter(storage) ? null : null;
  }

  const filter: RiderResultsRetainedFilter = {
    riderNames: cleanedRiderNames,
    horseNames: cleanedHorseNames,
    updatedAt: new Date().toISOString(),
    version: 4,
  };

  try {
    storage.setItem(RIDER_RESULTS_RETAINED_FILTER_KEY, JSON.stringify(filter));
    return filter;
  } catch {
    return null;
  }
};

export const clearRiderResultsRetainedFilter = (storage: Storage = window.localStorage): boolean => {
  try {
    storage.removeItem(RIDER_RESULTS_RETAINED_FILTER_KEY);
    return true;
  } catch {
    return false;
  }
};
